import { ArticleExtraData } from "../../../../../../../articlePage/stateInterfaces";
import { MediaContentTypes } from "../../../../../../../constants/contentTypes";
import { SiteItemData, SiteVideoData } from "../../../../../../interfaces";
import { VideoBehaiforData } from "../../../../../../commonDataObjects/interfaces";
import { ArticleDataJsonVer2, ItemJsonVer2, MediaDataJsonVersion2 } from "../../../../../../jsonSerializer/widgetJsonInterfacesVer2";
import { ImageDataJson } from "../../../../../../jsonSerializer/widgetJsonInterfaces";

const DEFAULT_BACKGROUND_IMAGE = "https://ynet-pic1.yit.co.il/Common/images/mivzak_reka.jpg";

const DEFAULT_VIDEO_BEHAVIOR: VideoBehaiforData = {
    isAutoPlaying: true,
    isLoopPlaying: false,
    isPlayerBarVisible: false,
    isSoundButtonVisible: false,
    isMuteAvailable: false,
};

export function mapItemJsonToArticleExtraData(article: ItemJsonVer2): ArticleExtraData {
    const { title, subTitle, roof, roofCount, text, linkData, mediaData } = article;
    const link = linkData.link as Partial<ArticleDataJsonVer2>;
    const { id, specialData, hasVideo, displayVideoIcon, catId, catName, mainCatId, subchannelName, pubDate, href, author } = link;

    const media = mediaData && mediaData.length > 0 ? mediaData[0] as MediaDataJsonVersion2 : null;
    let promotionImageDetails = null;
    let video = undefined;

    if (media && media.type === "video" && media.item) {
        const videoItem = media.item as any;
        video = [{
            videoName: title,
            credit: videoItem.credit || "",
            main_link: videoItem.url,
            low_res_link: videoItem.url,
            duration: videoItem.duration,
            promoPath: videoItem.promoUrl || "",
            aspectRatio: videoItem.mediaProportion || "",
            mediaVideoId: String(videoItem.videoId || ""),
            posterSrc: videoItem.opening_img && videoItem.opening_img.src ? videoItem.opening_img.src : videoItem.src,
        }];
    }

    if (media && media.type === "image") {
        const imageItem = media.item as ImageDataJson;
        promotionImageDetails = {
            url: imageItem.src,
            alt: imageItem.alt || "",
            credit: imageItem.credit || "",
            publishedLink: imageItem.src || "",
            caption: (imageItem as any).caption || "",
            aspectRatio: (imageItem as any).mediaProportion || "",
        };
    }

    return {
        articleId: String(id || ""),
        title,
        subTitle,
        roof,
        roofCount,
        plainText: text,
        author,
        publishedLink: href,
        dateUpdatedOnSite: pubDate,
        launchDate: pubDate,
        dateUpdated: specialData && specialData.dateUpdatedOnSite,
        promotionImage: promotionImageDetails ? promotionImageDetails.url || "" : "",
        promotionImageDetails,
        firstArticleImageDetails: null,
        hasVideo,
        displayVideoIcon,
        video,
        specialData,
        category: catName ? { id: catId, textToShow: catName } as any : null,
        premiumExpireDays: 0,
        subchannelName,
    } as ArticleExtraData;
}

export function mapArticleExtraDataToOverlayItems(
    articles: ArticleExtraData[],
    verticalBackgroundImage?: string,
): SiteItemData[] {
    if (!articles || articles.length === 0) return [];

    return articles.map(article => {
        const { articleId, roof, title, plainText, author, video, publishedLink, promotionImageDetails, specialData, dateUpdatedOnSite } = article;
        const videoCredit = (specialData && specialData.videoCredit) || (video && video[0] ? video[0].credit : "");
        const videoOriginalCaption = specialData && specialData.videoOriginalCaption;

        return {
            roofTitle: roof,
            title,
            text: plainText,
            author,
            itemId: articleId,
            publishedLink,
            dateUpdatedOnSite,
            media: buildMedia(title, video, videoCredit, videoOriginalCaption, promotionImageDetails, verticalBackgroundImage),
        } as SiteItemData;
    });
}

function buildMedia(
    title: string,
    video: any[],
    videoCredit: string,
    videoOriginalCaption: string,
    promotionImageDetails: any,
    verticalBackgroundImage?: string,
) {
    let videoMedia: SiteVideoData;
    let imageMedia: any;

    if (video && video[0]) {
        videoMedia = {
            ...DEFAULT_VIDEO_BEHAVIOR,
            credit: videoCredit,
            url: video[0].main_link,
            poster: video[0].posterSrc,
            duration: video[0].duration,
            videoType: "vertical_video",
            promoPath: video[0].promoPath,
            mediaId: video[0].mediaVideoId,
            videoPageId: video[0].videoPageId,
            aspectRatio: video[0].aspectRatio,
            downGradeUrl: video[0].low_res_link,
            title: title || videoOriginalCaption,
            mediaType: MediaContentTypes.MEDIA_VIDEO,
        } as SiteVideoData;
    }

    if (promotionImageDetails) {
        const isDefault = promotionImageDetails.alt === "default image";
        const backgroundImage = verticalBackgroundImage || DEFAULT_BACKGROUND_IMAGE;

        imageMedia = {
            isDefaultImage: isDefault,
            alt: promotionImageDetails.alt || "",
            mediaType: MediaContentTypes.MEDIA_IMAGE,
            credit: promotionImageDetails.credit || "",
            caption: promotionImageDetails.caption || "",
            url: isDefault ? backgroundImage : promotionImageDetails.url,
            publishedLink: isDefault ? backgroundImage : promotionImageDetails.publishedLink,
            aspectRatio: isDefault ? "9:16" : getAspectRatio(promotionImageDetails),
        };
    }

    return videoMedia ? videoMedia : imageMedia;
}

const getAspectRatio = (promotionImageDetails: any) => {
    if (promotionImageDetails.aspectRatio && promotionImageDetails.aspectRatio !== "") {
        return promotionImageDetails.aspectRatio;
    } else if (promotionImageDetails.width && promotionImageDetails.height) {
        return promotionImageDetails.height > promotionImageDetails.width ? "9:16" : "16:9";
    } else return ""
};

export interface OngoingRoofItemMeta {
    isEventRoof: boolean
    showFilledRedDot: boolean
    showWhiteDot: boolean
    showTimeline: boolean
    hideRoof: boolean
    isClusterStart: boolean
    isInMultiCluster: boolean
    clusterSize: number
    clusterRoof: string | null
}

const emptyOngoingMeta = (): OngoingRoofItemMeta => ({
    isEventRoof: false,
    showFilledRedDot: false,
    showWhiteDot: false,
    showTimeline: false,
    hideRoof: false,
    isClusterStart: false,
    isInMultiCluster: false,
    clusterSize: 1,
    clusterRoof: null,
});

/** Trims and collapses excess whitespace so roof titles match for clustering. */
export function normalizeRoofTitle(roof: string | null | undefined): string {
    if (!roof) {
        return "";
    }
    return roof.trim() //.replace(/\s+/g, " "); possible solution to remove excess whitespace inside the string
}

/**
 * Computes per-item ongoing-event presentation for the visible ticker list only.
 * A roof qualifies only when it has at least one consecutive multi-item cluster (2+)
 * among the given articles (and is not excluded). Isolated singles of that roof then
 * get a timeline line; two or more non-consecutive singles with no visible cluster do not.
 * Callers should pass the current visible slice so a new flash item triggers recalculation.
 */
export function buildOngoingRoofPresentation(articles: ArticleExtraData[], excludedRoofs: string[] = []): OngoingRoofItemMeta[] {
    if (!articles || articles.length === 0) {
        return [];
    }

    const excluded = (excludedRoofs || []).map(normalizeRoofTitle).filter(Boolean);
    const eventRoofs: { [roof: string]: true } = {};

    // Qualify roofs by consecutive runs in the visible list, not total occurrence count.
    let scan = 0;
    while (scan < articles.length) {
        const roof = normalizeRoofTitle(articles[scan].roof);
        if (!roof || excluded.indexOf(roof) !== -1) {
            scan++;
            continue;
        }

        let end = scan;
        while (end < articles.length && normalizeRoofTitle(articles[end].roof) === roof) {
            end++;
        }

        if (end - scan >= 2) {
            eventRoofs[roof] = true;
        }
        scan = end;
    }

    const firstOccurrence: { [roof: string]: number } = {};
    articles.forEach((article, index) => {
        const roof = normalizeRoofTitle(article.roof);
        if (roof && eventRoofs[roof] && firstOccurrence[roof] === undefined) {
            firstOccurrence[roof] = index;
        }
    });

    const result: OngoingRoofItemMeta[] = articles.map(() => emptyOngoingMeta());
    let i = 0;
    while (i < articles.length) {
        const roof = normalizeRoofTitle(articles[i].roof);
        if (!roof || !eventRoofs[roof]) {
            i++;
            continue;
        }

        let end = i;
        while (end < articles.length && normalizeRoofTitle(articles[end].roof) === roof) {
            end++;
        }

        const clusterSize = end - i;
        const isMulti = clusterSize >= 2;

        for (let k = i; k < end; k++) {
            result[k] = {
                isEventRoof: true,
                showFilledRedDot: k === firstOccurrence[roof],
                showWhiteDot: true,
                showTimeline: true,
                hideRoof: isMulti,
                isClusterStart: k === i && isMulti,
                isInMultiCluster: isMulti,
                clusterSize,
                clusterRoof: roof,
            };
        }
        i = end;
    }

    return result;
}

export function filterArticlesByRoof(articles: ArticleExtraData[], roof: string): ArticleExtraData[] {
    if (!roof || !articles || articles.length === 0) {
        return [];
    }
    const normalizedRoof = normalizeRoofTitle(roof);
    return articles.filter(article => normalizeRoofTitle(article.roof) === normalizedRoof);
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

function is24HoursLimitDisabled(): boolean {
    return typeof window !== "undefined" && !!window.disable24hourslimit;
}

export function isArticleWithinLast24Hours(article: ArticleExtraData): boolean {
    const updatedTime = new Date(article.dateUpdatedOnSite).getTime();
    if (isNaN(updatedTime)) {
        return false;
    }
    return (Date.now() - updatedTime) <= TWENTY_FOUR_HOURS_MS;
}

/** Roof-matched popup items, limited to the last 24 hours unless `window.disable24hourslimit` is set. */
export function filterOngoingRoofPopupArticles(articles: ArticleExtraData[], roof: string): ArticleExtraData[] {
    const relatedArticles = filterArticlesByRoof(articles, roof);
    if (is24HoursLimitDisabled()) {
        return relatedArticles;
    }
    return relatedArticles.filter(isArticleWithinLast24Hours);
}
